import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Layout } from './components/layout/Layout';
import CyclingMapPage from './pages/CyclingMapPage';
import MyRoutesPage from './pages/MyRoutesPage';
import { SettingsPage } from './pages/SettingsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { ImprintPage } from './pages/ImprintPage';
import { ContactPage } from './pages/ContactPage';
import RideResponsiblyPage from './pages/RideResponsiblyPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import SessionHandler from './components/SessionHandler';
import PasswordRecoveryModal from './components/PasswordRecoveryModal';
import GlobalAuthModal from './components/GlobalAuthModal';
import RecoveryGuard from './components/RecoveryGuard';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import PageErrorBoundary from './components/PageErrorBoundary';
import globalErrorHandler from './services/globalErrorHandler';
import { useEffect } from 'react';
import cyclingBuddyTheme from './theme';

const App: React.FC = () => {
  // Initialize global error handlers only once
  useEffect(() => {
    globalErrorHandler.init();
    
    // Cleanup on unmount
    return () => {
      globalErrorHandler.cleanup();
    };
  }, []); // Empty dependency array - only run once

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider theme={cyclingBuddyTheme}>
          <CssBaseline />
          <ToastProvider defaultPosition="bottom-center" maxToasts={3}>
            <AuthProvider>
              <SessionHandler />
              <PasswordRecoveryModal />
              <GlobalAuthModal />
              <RecoveryGuard>
                <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Layout />}>
                    {/* Main app pages - wrapped with single page error boundary */}
                    <Route index element={
                      <PageErrorBoundary pageName="Cycling Map" pageType="map">
                        <CyclingMapPage />
                      </PageErrorBoundary>
                    } />
                    <Route path="route/:routeId" element={
                      <PageErrorBoundary pageName="Cycling Map" pageType="map">
                        <CyclingMapPage />
                      </PageErrorBoundary>
                    } />
                    <Route path="my-routes" element={
                      <PageErrorBoundary pageName="My Routes" pageType="routes" fallbackPath="/">
                        <MyRoutesPage />
                      </PageErrorBoundary>
                    } />
                    <Route path="settings" element={
                      <PageErrorBoundary pageName="Settings" pageType="settings" fallbackPath="/">
                        <SettingsPage />
                      </PageErrorBoundary>
                    } />
                    
                    {/* Static pages - can share a generic boundary */}
                    <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="ride-responsibly" element={<RideResponsiblyPage />} />
                    <Route path="legal" element={<RideResponsiblyPage />} /> {/* Redirect old URL */}
                    <Route path="imprint" element={<ImprintPage />} />
                    <Route path="contact" element={<ContactPage />} />
                  </Route>
                  
                  {/* Auth pages - separate boundary for auth-specific handling */}
                  <Route path="/auth/callback" element={
                    <PageErrorBoundary pageName="Authentication" pageType="auth">
                      <AuthCallbackPage />
                    </PageErrorBoundary>
                  } />
                  <Route path="/auth/v1/verify" element={
                    <PageErrorBoundary pageName="Authentication" pageType="auth">
                      <EmailVerificationPage />
                    </PageErrorBoundary>
                  } />
                </Routes>
              </BrowserRouter>
            </RecoveryGuard>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};export default App;